// One notification row, shared by the bell dropdown and the /notifications page (Phase 46, 46-20).
// UI-SPEC contract 2. Every string comes from the shared copy file; titles are plain text nodes
// (never HTML) because they carry other users' names and store names (T-46-20-01).

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Award, BadgeCheck, CalendarCheck, FileX, Heart, MessageCircle, ShieldAlert, Sparkles, Star, Store, type LucideIcon } from "lucide-react";
import type { NotificationType } from "@shared/notifications";
import { NOTIFICATION_COPY, formatNotificationTime, renderNotificationTitle } from "@shared/notificationCopy";
import { Link } from "@/components/Link";
import { markNotificationRead, type ClientNotification } from "@/api/notificationService";
import { NOTIFICATION_QUERY_KEYS, safeDeepLink } from "@/lib/notificationUi";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";

export type NotificationListSurface = "bell_dropdown" | "inbox_page";

type IconSpec = {
  Icon: LucideIcon;
  color: string;
  bg: string;
  fill?: string;
};

const NEUTRAL = { color: "var(--ys-ink-2)", bg: "var(--ys-bg-2)" };

const ICONS: Record<NotificationType, IconSpec> = {
  claim_approved: { Icon: BadgeCheck, color: "var(--ys-success)", bg: "var(--ys-success-soft)" },
  claim_rejected: { Icon: FileX, ...NEUTRAL },
  store_review_received: { Icon: Star, color: "var(--ys-star)", bg: "var(--ys-bg-2)", fill: "var(--ys-star)" },
  review_merchant_replied: { Icon: Store, ...NEUTRAL },
  review_commented: { Icon: MessageCircle, ...NEUTRAL },
  review_liked: { Icon: Heart, ...NEUTRAL },
  tier_reached: { Icon: Award, color: "var(--ys-success)", bg: "var(--ys-success-soft)" },
  points_awarded: { Icon: Sparkles, ...NEUTRAL },
  escalation_opened: { Icon: ShieldAlert, ...NEUTRAL },
  // Phase 49 onboarding (UI-SPEC S6): neutral circle, accent stays reserved for unread state.
  welcome: { Icon: Sparkles, ...NEUTRAL },
  onboarding_day3: { Icon: CalendarCheck, ...NEUTRAL },
  onboarding_day7: { Icon: CalendarCheck, ...NEUTRAL },
};

type Props = {
  notification: ClientNotification;
  surface: NotificationListSurface;
  onNavigate?: () => void;
};

export default function NotificationRow({ notification, surface, onNavigate }: Props) {
  const queryClient = useQueryClient();
  const { trackNotifications } = usePostHogTracking();
  const [localRead, setLocalRead] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Public component: admin-audience rows belong to the admin bell (46-22).
  if (notification.audience !== "public") return null;

  const isRead = notification.read || localRead;
  const { type, payload, actorNames, actorCount } = notification;
  const title = renderNotificationTitle({ type, payload, actorNames, actorCount });
  const time = formatNotificationTime(notification.lastEventAt);
  const extra = NOTIFICATION_COPY[type]?.metaLine?.({ payload: payload ?? {}, actorNames, actorCount }) ?? "";
  const icon: IconSpec = ICONS[type] ?? { Icon: Sparkles, ...NEUTRAL };
  const href = safeDeepLink(notification.deepLink);

  // Fire-and-forget: navigation must not wait on the read receipt.
  const markRead = async () => {
    const res = await markNotificationRead(notification.id);
    if (res.ok) await queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.list("public") });
  };

  const handleClick = () => {
    if (!isRead) {
      setLocalRead(true);
      void markRead();
    }
    trackNotifications.clicked({ type, surface });
    onNavigate?.();
  };

  const background = hovered ? "var(--ys-bg-2)" : isRead ? "var(--ys-paper)" : "var(--ys-primary-soft)";

  return (
    <Link
      to={href}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        padding: 16,
        background,
        borderBottom: "1px solid var(--ys-rule)",
        borderInlineStart: `2px solid ${hovered && !isRead ? "var(--ys-primary)" : "transparent"}`,
        textDecoration: "none",
        fontFamily: "var(--ys-font)",
        transition: "background 150ms",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: icon.bg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <icon.Icon size={16} color={icon.color} fill={icon.fill ?? "none"} />
      </span>

      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            lineHeight: 1.6,
            fontWeight: isRead ? 400 : 600,
            color: isRead ? "var(--ys-ink-2)" : "var(--ys-ink)",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </span>
        {extra && (
          <span
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              color: "var(--ys-ink-3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {extra}
          </span>
        )}
        <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--ys-ink-3)" }}>{time}</span>
      </span>

      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          marginTop: 8,
          flexShrink: 0,
          background: isRead ? "transparent" : "var(--ys-primary)",
        }}
      />
    </Link>
  );
}
